/**
 * 收录台账页面 — IndexLedger（F5，对齐概念稿 index-ledger.html）。
 *
 * GET  /api/index-ledger/summary?product_id  三层漏斗聚合（4 指标卡 + FunnelView + 断点说明）
 * GET  /api/index-ledger?product_id&status&channel&page&size  URL 池列表
 * POST /api/index-ledger/urls                手工录入 URL（轻量写 LR1，返回导入报告三类清单）
 *
 * 结构：4 指标卡 → g83（漏斗 + 断点诊断）→ 工具栏（状态/渠道筛选 + 手工录入）→ URL 池表。
 * 层级语义：L1 搜索引擎收录 → L2 AI 可检索 → L3 被引用，卡在哪层治哪层。
 * 后端端点并行开发中：全部按契约盲写 + EmptyState v2 降级，不红屏。
 */
import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { AddOutlined } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import StatCard from "../components/StatCard";
import DataTable, { type DataTableColumn } from "../components/DataTable";
import Badge from "../components/Badge";
import Loading from "../components/Loading";
import EmptyState from "../components/EmptyState";
import FunnelView from "../components/FunnelView";
import {
  getIndexLedgerSummary,
  getIndexLedger,
  postIndexLedgerUrls,
} from "../api/indexLedger";
import { useCompanyStore } from "../store/companyStore";
import { LEDGER_STATUS_MAP } from "../utils/constants";
import { formatDate, formatDateTime } from "../utils/format";
import type {
  LedgerSummary,
  LedgerUrlImportResult,
  LedgerUrlItem,
} from "../types";

/** 状态筛选选项（值 = 后端 status 枚举）。 */
const STATUS_OPTIONS = [
  { value: "", label: "全部状态" },
  { value: "indexed", label: "已收录" },
  { value: "pending", label: "复检中" },
  { value: "abnormal", label: "未收录" },
];

/** 渠道选项（中文枚举，与后端 channel 对齐）。 */
const CHANNEL_OPTIONS = [
  "百家号",
  "知乎",
  "搜狐号",
  "公众号",
  "网易号",
  "头条号",
  "36氪",
  "其他",
];

/** 收录状态徽章（indexed 成功 / pending 信息 / abnormal 错误 / 其他灰）。 */
function LedgerStatusBadge({ status }: { status: string }) {
  const info = LEDGER_STATUS_MAP[status] || {
    label: status || "未知",
    color: "text.secondary",
    bg: "semanticBg.neutral",
  };
  return <Badge label={info.label} color={info.color} bgColor={info.bg} />;
}

export default function IndexLedger() {
  const selectedProductId = useCompanyStore((s) => s.selectedProductId);

  // 漏斗聚合
  const [summary, setSummary] = useState<LedgerSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [summaryFailed, setSummaryFailed] = useState(false);

  // URL 池列表
  const [statusFilter, setStatusFilter] = useState("");
  const [channelFilter, setChannelFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [items, setItems] = useState<LedgerUrlItem[]>([]);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [listFailed, setListFailed] = useState(false);

  // 手工录入对话框
  const [dialogOpen, setDialogOpen] = useState(false);
  const [entryChannel, setEntryChannel] = useState(CHANNEL_OPTIONS[0]);
  const [entryText, setEntryText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [importResult, setImportResult] = useState<LedgerUrlImportResult | null>(null);
  const [submitError, setSubmitError] = useState("");

  /** 漏斗聚合加载。 */
  useEffect(() => {
    if (!selectedProductId) {
      setSummary(null);
      setSummaryLoading(false);
      return;
    }
    setSummaryLoading(true);
    setSummaryFailed(false);
    getIndexLedgerSummary(selectedProductId)
      .then(setSummary)
      .catch(() => {
        setSummary(null);
        setSummaryFailed(true);
      })
      .finally(() => setSummaryLoading(false));
  }, [selectedProductId]);

  /** URL 池列表加载。 */
  const fetchList = useCallback(async () => {
    if (!selectedProductId) {
      setItems([]);
      setTotal(0);
      setListLoading(false);
      return;
    }
    setListLoading(true);
    setListFailed(false);
    try {
      const result = await getIndexLedger({
        product_id: selectedProductId,
        status: statusFilter || undefined,
        channel: channelFilter || undefined,
        page: page + 1,
        size: rowsPerPage,
      });
      setItems(result.items || []);
      setTotal(result.total || 0);
    } catch {
      setItems([]);
      setTotal(0);
      setListFailed(true);
    } finally {
      setListLoading(false);
    }
  }, [selectedProductId, statusFilter, channelFilter, page, rowsPerPage]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  /** 手工录入提交：每行一个 URL（可跟空格 + 标题）。 */
  const handleSubmitUrls = async () => {
    if (!selectedProductId) return;
    const urls = entryText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [url, ...rest] = line.split(/\s+/);
        return { url, title: rest.join(" ") || undefined, channel: entryChannel };
      });
    if (urls.length === 0) {
      setSubmitError("请至少输入一个 URL");
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    setImportResult(null);
    try {
      const result = await postIndexLedgerUrls({
        product_id: selectedProductId,
        urls,
      });
      setImportResult(result);
      fetchList();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "录入失败，请稍后重试");
    } finally {
      setSubmitting(false);
    }
  };

  const statusCounts = summary?.status_counts || {};
  const broken =
    summary !== null && summary.index_rate < summary.baseline_index_rate;

  const columns: DataTableColumn<LedgerUrlItem>[] = [
    {
      key: "title",
      label: "文章标题",
      render: (row) => (
        <Box>
          <Typography sx={{ fontSize: 13, fontWeight: 500 }}>
            {row.title || row.url}
          </Typography>
          {row.title && (
            <Typography
              sx={{
                fontSize: 11,
                color: "text.disabled",
                maxWidth: 360,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.url}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: "channel",
      label: "渠道",
      render: (row) => (
        <Typography sx={{ fontSize: 13 }}>{row.channel || "—"}</Typography>
      ),
    },
    {
      key: "published_at",
      label: "发布日期",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDate(row.published_at)}
        </Typography>
      ),
    },
    {
      key: "status",
      label: "收录状态",
      align: "center",
      render: (row) => <LedgerStatusBadge status={row.status} />,
    },
    {
      key: "last_check_at",
      label: "最近复检",
      render: (row) => (
        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
          {formatDateTime(row.last_check_at)}
        </Typography>
      ),
    },
    {
      key: "check_day_offset",
      label: "T+n",
      align: "right",
      render: (row) => (
        <Typography sx={{ fontSize: 12 }}>
          {row.check_day_offset !== undefined && row.check_day_offset !== null
            ? `T+${row.check_day_offset}`
            : "—"}
        </Typography>
      ),
    },
    {
      key: "source",
      label: "来源",
      align: "center",
      render: (row) =>
        row.source === "manual" ? (
          <Badge label="手工" color="primary.main" bgColor="brand.50" />
        ) : (
          <Badge label={row.source === "auto" ? "自动" : row.source || "—"} />
        ),
    },
  ];

  if (!selectedProductId) {
    return (
      <Box>
        <PageHeader
          title="收录台账"
          breadcrumbs={[{ label: "GEO 分析" }, { label: "收录台账" }]}
        />
        <Card>
          <CardContent>
            <EmptyState
              text="请先在顶栏选择产品"
              subtitle="收录台账按产品维度聚合，选择产品后自动加载"
            />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="收录台账"
        breadcrumbs={[{ label: "GEO 分析" }, { label: "收录台账" }]}
        subtitle="L1 搜索引擎收录 → L2 AI 可检索 → L3 被引用 · 卡在哪层治哪层"
      />

      {/* 4 指标卡 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="URL 池"
            value={summary ? summary.produced : "—"}
            subtitle="本月投放 · 手工 + 导入 + 标记自有"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="已收录"
            value={summary ? summary.indexed : "—"}
            subtitle={
              summary ? `收录率 ${(summary.index_rate * 100).toFixed(1)}%` : undefined
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="复检中"
            value={summary ? (statusCounts.pending ?? 0) : "—"}
            subtitle="T+1 / T+3 / T+7 递进复检"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="未收录"
            value={summary ? (statusCounts.abnormal ?? 0) : "—"}
            subtitle={broken ? "收录率低于基线 · 断点预警" : "T+7 未过转异常降频周检"}
          />
        </Grid>
      </Grid>

      {/* g83：漏斗 + 断点诊断 */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h4" sx={{ mb: 2 }}>
                三层漏斗 · 本月
              </Typography>
              {summaryLoading ? (
                <Loading text="正在加载漏斗..." />
              ) : summaryFailed || !summary ? (
                <EmptyState
                  text="收录漏斗暂未接入"
                  source="由洞悉引擎 v1.5 收录管道产出 · 未接入时显示空态"
                />
              ) : (
                <FunnelView summary={summary} />
              )}
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography variant="h4">断点诊断</Typography>
                {broken && <Badge label="低于基线" variant="warning" />}
              </Box>
              {summaryFailed || !summary ? (
                <EmptyState
                  text="断点诊断暂未接入"
                  source="由洞悉引擎 v1.5 收录管道产出"
                />
              ) : (
                <>
                  <Typography sx={{ fontSize: 13, lineHeight: 1.8 }}>
                    {broken
                      ? `收录率 ${(summary.index_rate * 100).toFixed(1)}% 低于基线 ${(summary.baseline_index_rate * 100).toFixed(0)}%，断点集中在搜索引擎收录层。建议按序排查：`
                      : "收录层运转正常。未收录 URL 建议按序排查："}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mt: 1.25,
                      fontSize: 13,
                    }}
                  >
                    <Typography sx={{ fontSize: 13 }}>
                      ① 查 URL 可达性与 robots / WAF 拦截
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      ② 百度站长平台手动提交（引导绑定取官方数据）
                    </Typography>
                    <Typography sx={{ fontSize: 13 }}>
                      ③ 修复后可拉动 AI 可检索层
                    </Typography>
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      lineHeight: 1.8,
                      mt: 1.5,
                      pt: 1.5,
                      borderTop: "1px solid",
                      borderColor: "lineSoft",
                    }}
                  >
                    检查失败标记「未知」，不误报未收录（LR2）；T+7
                    仍未收录转异常区并降频周检（LR1）。
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* 工具栏：状态/渠道筛选 + 手工录入 */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 2, "&:last-child": { pb: 2 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              select
              size="small"
              label="收录状态"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 140 }}
            >
              {STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="渠道"
              value={channelFilter}
              onChange={(e) => {
                setChannelFilter(e.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">全部渠道</MenuItem>
              {CHANNEL_OPTIONS.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddOutlined />}
              onClick={() => {
                setDialogOpen(true);
                setImportResult(null);
                setSubmitError("");
              }}
            >
              手工录入 URL
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* URL 池表 */}
      {listLoading ? (
        <Loading fullPage />
      ) : listFailed ? (
        <Card>
          <CardContent>
            <EmptyState
              text="URL 池暂未接入"
              source="由洞悉引擎 v1.5 三级话术管道产出 · 端点未就绪时显示空态"
              hint="未接入自动管道前可使用「手工录入 URL」"
            />
          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={items}
          total={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(size) => {
            setRowsPerPage(size);
            setPage(0);
          }}
          getRowId={(row) => row.id}
          emptyText="URL 池为空 · 可通过「手工录入 URL」添加"
        />
      )}

      {/* 手工录入对话框 */}
      <Dialog
        open={dialogOpen}
        onClose={() => !submitting && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>手工录入 URL</DialogTitle>
        <DialogContent>
          <TextField
            select
            size="small"
            label="渠道"
            value={entryChannel}
            onChange={(e) => setEntryChannel(e.target.value)}
            sx={{ minWidth: 160, mb: 2, mt: 0.5 }}
          >
            {CHANNEL_OPTIONS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            multiline
            minRows={6}
            fullWidth
            placeholder={"每行一个 URL；可在 URL 后加空格再写标题（可选）\nhttps://example.com/a 文章标题"}
            value={entryText}
            onChange={(e) => setEntryText(e.target.value)}
          />
          <Typography sx={{ fontSize: 11, color: "text.disabled", mt: 1 }}>
            服务端做 URL 归一化 + 去重，重复自动跳过；录入后进入 T+1/T+3/T+7 复检池。
          </Typography>
          {submitError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {submitError}
            </Alert>
          )}
          {importResult && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              录入完成：新增 {importResult.created} 条 · 重复跳过{" "}
              {importResult.duplicated} 条 · 失败 {importResult.failed.length} 条
              {importResult.failed.length > 0 && (
                <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
                  {importResult.failed.map((f) => (
                    <li key={f.url}>
                      <Typography sx={{ fontSize: 12 }}>
                        {f.url}：{f.reason}
                      </Typography>
                    </li>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={submitting}>
            关闭
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitUrls}
            disabled={submitting || !entryText.trim()}
          >
            {submitting ? "提交中..." : "提交"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
